'use client'

import { CheckmarkShield12 } from '@vinted/monochrome-icons'
import { Cell, ColorCode, Icon, Image, Spacer, Text } from '@vinted/web-ui'
import { noop } from 'lodash'
import { Fragment } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useConversationContext,
  useConversationSidebarContext,
} from '@marketplace-web/messaging/conversation-providers-feature'
import { FormattedRelativeTime, useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { findThumbnail } from '@marketplace-web/shared/utils'
import {
  InboxConversationListItemDto,
  CONVERSATION_URL,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { EscrowOrderTrackingEntrySource } from '@marketplace-web/order-details/order-details-data'
import { colorCodeToHex } from '@marketplace-web/vendor-abstractions/web-ui-util'

type Props = {
  conversation: InboxConversationListItemDto
  onClick?: () => void
}

enum ItemThumbnailSize {
  Medium = 'thumb150x210',
}

type ItemPhotoDto = {
  id: number
  thumbnails: Array<{
    type: ItemThumbnailSize | string
    url: string
  }>
}

const InboxListItem = ({ conversation, onClick = noop }: Props) => {
  const { conversation: activeConversation, getConversationRequest } = useConversationContext()
  const { closeSidebar } = useConversationSidebarContext()
  const { user } = useSession()
  const translate = useTranslate('conversations.list')
  const inboxTranslate = useTranslate('inbox')
  const navigate = useNavigate()
  const asset = useAsset('assets/no-photo')

  const userId = user?.id

  const isConversationActive = conversation.id === activeConversation?.id
  const oppositeUser = conversation.opposite_user

  const { id } = conversation

  const renderPrefix = () => {
    return (
      <Image
        role="img"
        src={findThumbnail(oppositeUser?.photo) || asset(EMPTY_USER_IMAGE_NAME)}
        size={Image.Size.Large}
        styling={Image.Styling.Circle}
      />
    )
  }

  const renderBadge = () => {
    if (oppositeUser?.badge !== 'moderator') return null

    return (
      <>
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Icon
          name={CheckmarkShield12}
          color={Icon.Color.Primary}
          aria={{
            'aria-label': inboxTranslate('a11y.moderator_badge'),
          }}
          testId="moderator-badge-icon"
        />
      </>
    )
  }

  const renderUsername = () => {
    if (!oppositeUser?.login) return translate('deleted_opposite_user.login')

    return (
      <>
        {oppositeUser.login}
        {renderBadge()}
      </>
    )
  }

  const renderTitle = () => {
    return <Text as="h2" type={Text.Type.Title} text={renderUsername()} />
  }

  const renderLastInteractionTime = () => {
    const conversationUpdatedAt = conversation.updated_at

    if (!conversationUpdatedAt) return null

    const createdAt = new Date(conversationUpdatedAt).getTime()

    return (
      <Text as="h3" type={Text.Type.Subtitle} text={<FormattedRelativeTime value={createdAt} />} />
    )
  }

  const renderRemainingItemCount = (itemCount: number, itemPhotosCount: number) => {
    const remainingItemCount = itemCount - itemPhotosCount

    return remainingItemCount > 0 ? (
      <Image
        label={`+${remainingItemCount}`}
        size={Image.Size.Medium}
        styling={Image.Styling.Rounded}
        color={colorCodeToHex(ColorCode.GreyscaleLevel3)}
      />
    ) : null
  }

  const getPhotoThumbnail = (photo: ItemPhotoDto) => {
    const thumbnail = photo?.thumbnails?.find(thumb => thumb.type === ItemThumbnailSize.Medium)

    return thumbnail?.url
  }

  const renderOrderItems = (
    itemPhotos: Array<ItemPhotoDto> | null | undefined,
    itemCount: number | null | undefined,
  ) => {
    if (!itemPhotos || !itemCount) return null

    return (
      <div>
        {itemPhotos.map(photo => (
          <Fragment key={photo.id}>
            <Image
              src={getPhotoThumbnail(photo)}
              size={Image.Size.Medium}
              styling={Image.Styling.Rounded}
            />
            <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
          </Fragment>
        ))}
        <div data-testid="inbox-order-items" className="u-inline-block">
          {renderRemainingItemCount(itemCount, itemPhotos.length)}
        </div>
      </div>
    )
  }

  const renderMessageContent = () => {
    const { description, item_photos, item_count } = conversation

    return (
      <>
        <Text as="span" truncate text={description} testId="inbox-list-item-description" />
        <Cell
          styling={Cell.Styling.Tight}
          theme="transparent"
          body={renderOrderItems(item_photos, item_count)}
        />
      </>
    )
  }

  const handleConversationClick = () => {
    closeSidebar()

    if (!userId || isConversationActive) return

    getConversationRequest({ conversationId: conversation.id })

    navigate(CONVERSATION_URL(conversation.id, EscrowOrderTrackingEntrySource.Inbox))

    onClick()
  }

  return (
    <div
      className={isConversationActive ? 'u-background-gray' : ''}
      data-testid={`inbox-list-item-${id}-container`}
    >
      <Cell
        title={renderTitle()}
        prefix={renderPrefix()}
        subtitle={renderLastInteractionTime()}
        body={renderMessageContent()}
        highlighted={conversation.unread}
        type={Cell.Type.Navigating}
        theme="transparent"
        onClick={handleConversationClick}
        testId={`inbox-list-item-${id}`}
      />
    </div>
  )
}

export default InboxListItem
